import 'react-autocomplete-input/dist/bundle.css';

import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useToggle } from 'react-use';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { REPORT_TYPE_TO_VALUE_CONTENT_OPTIONS } from 'src/components/Reports/ReportsTableEditor/ReportsTableEditorForm.constants';
import { useAppContext } from 'src/contexts/AppContext';
import {
  Content,
  isContentId,
  useContent,
  useContentContext,
  useContents,
} from 'src/contexts/ContentContext';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { typography } from 'src/core/themes/shared.css';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  applyWindowFunction,
  detectAggregateFunction,
  detectWindowFunction,
  expressionToTokens,
  tokensToExpression,
} from 'src/utils/columns/reportV2CustomColumnUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { ContentIds } from 'src/utils/constants/contentIdDataMap';
import { WINDOW_FUNCTION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import { hasAllPermissions } from 'src/utils/userPermissionUtils';
import { hasFeatures } from 'src/utils/userUtils';
import { Feature, ReportColumn } from 'src/WebApiController';

import { Modal, ModalBody, ModalFooter, ModalHeader } from '../../Modal';
import * as styles from '../TableColumns.css';
import { CustomColumnInstructionPopover } from './Components/CustomColumnInstructionPopover';
import { FormulaBuilderInput } from './Components/FormulaBuilderInput';
import {
  AggregateFunction,
  CustomColumnType,
  WindowFunction,
} from './TableCustomColumnModal.types';
import {
  getDetaultValueForColumnInput,
  getPersonalizationConfigFromFormulaFieldId,
  REPORT_TYPE_TO_FORMULA_COLUMNS,
  validateNewCustomColumn,
} from './TableCustomColumnModalReporting.utils';

export function TableCustomColumnModalReporting({
  onClose,
  columnName,
  columnValue,
  onSave,
  reportType,
  existingCustomColumns,
}: {
  onClose: () => void;
  columnName: string | undefined;
  columnValue: string | undefined;
  onSave: (columnId: string, displayName: string, columnValue: string) => void;
  reportType: ReportTypesV2;
  existingCustomColumns: ReportColumn[];
}) {
  const [isOpen, toggleIsOpen] = useToggle(true);
  const [showMetricSelector, setShowMetricSelector] = useState(false);
  const [selectedWindowFunction, setSelectedWindowFunction] =
    useState<WindowFunction>(WindowFunction.NONE);
  const [aggregateFunction, setAggregateFunction] = useState(
    AggregateFunction.SUM
  );
  const { appContext, loginContext } = useAppContext();
  const { contentResolver } = useContentContext();
  const hasRatioToReportFeature = useUserHasFeature(
    Feature.RatioToReportCustomCol
  );

  const nameColumnPlaceholder = useContent(ContentId.NameColumnPlaceholder);
  const requiredMsg = useContent(ContentId.Required);
  const invalidFormulaMsg = useContent(ContentId.InvalidFormula);
  const columnWithSameNameExistsMsg = useContent(
    ContentId.ColumnWithSameNameExists
  );

  const allPresetColumnsMap = REPORT_TYPE_TO_VALUE_CONTENT_OPTIONS[
    reportType
  ] as Record<string, ContentId>;

  const allPresetColumnCid = new Set([...Object.values(allPresetColumnsMap)]);
  const allPresetColumnNames = useContents(
    Array.from(allPresetColumnCid.values())
  );

  const fieldOptionsPreset = REPORT_TYPE_TO_FORMULA_COLUMNS[reportType];

  const filteredFormulaFields = useMemo(
    () =>
      fieldOptionsPreset
        .filter((field) => {
          const columnDef = getPersonalizationConfigFromFormulaFieldId(
            reportType,
            field
          );

          const hasFeatureForColumn =
            (columnDef?.requiredFeatures?.length ?? 0) === 0 ||
            hasFeatures(
              loginContext?.user,
              appContext?.features,
              columnDef!.requiredFeatures!
            );

          const hasPermissionForColumn =
            (columnDef?.requiredPermissions?.length ?? 0) === 0 ||
            hasAllPermissions(
              loginContext?.user,
              columnDef!.requiredPermissions!
            );

          return hasFeatureForColumn && hasPermissionForColumn;
        })
        .reduce<Record<string, ContentId>>(
          (acc, field) => ({
            ...acc,
            [field]:
              allPresetColumnsMap[field as keyof typeof allPresetColumnsMap],
          }),
          {}
        ),
    [
      allPresetColumnsMap,
      appContext?.features,
      fieldOptionsPreset,
      loginContext?.user,
      reportType,
    ]
  );

  const fieldOptions = useMemo(
    () => ({
      ...filteredFormulaFields,
    }),
    [filteredFormulaFields]
  );

  const fieldOptionsReverse = useMemo(() => {
    return Object.entries(fieldOptions).reduce<Record<string, string>>(
      (acc, entry) => {
        const [key, value] = entry;
        if (!isContentId(value)) {
          acc[value] = key;
          return acc;
        }

        const content = contentResolver(
          ContentIds[value as ContentId]
        ) as string;
        acc[content] = key;
        return acc;
      },
      {}
    );
  }, [contentResolver, fieldOptions]);

  const methods = useForm<CustomColumnType>({
    defaultValues: getDetaultValueForColumnInput(columnName, columnValue),
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = methods;

  const name = watch('name');
  const [formulaTokens, setFormulaTokens] = useState<string[]>([]);
  const [formulaCaretPosition, setFormulaCaretPosition] = useState<number>(-1);

  const onSaveHandler = (newCustomColumn: CustomColumnType) => {
    newCustomColumn.name = newCustomColumn.name.trim();

    // Convert tokens to expression using the selected aggregate function
    newCustomColumn.formula = tokensToExpression(
      formulaTokens,
      fieldOptionsPreset,
      aggregateFunction
    ).trim();

    if (
      !validateNewCustomColumn(
        newCustomColumn.name,
        newCustomColumn.formula,
        fieldOptionsPreset,
        allPresetColumnNames,
        existingCustomColumns,
        newCustomColumn.id,
        setError,
        columnWithSameNameExistsMsg,
        invalidFormulaMsg,
        requiredMsg
      )
    )
      return;

    const finalFormula =
      selectedWindowFunction !== WindowFunction.NONE
        ? applyWindowFunction(newCustomColumn.formula, selectedWindowFunction)
        : newCustomColumn.formula;

    onSave(newCustomColumn.id, newCustomColumn.name, finalFormula);
    toggleIsOpen();
  };

  useEffect(() => {
    if (columnValue && fieldOptionsPreset.length) {
      const { windowFunction, innerExpression } =
        detectWindowFunction(columnValue);

      if (windowFunction) {
        setSelectedWindowFunction(windowFunction as WindowFunction);
      }

      const { aggregateFunction: detectedAggregateFunction } =
        detectAggregateFunction(innerExpression);

      setAggregateFunction(detectedAggregateFunction);
      setFormulaTokens(expressionToTokens(innerExpression, fieldOptionsPreset));
    }
  }, [columnValue, fieldOptionsPreset]);

  useEffect(() => {
    if (!isOpen) setTimeout(onClose, 300);
  }, [isOpen, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={toggleIsOpen}
      unmountOnClose
      centered
      size="sm"
      onClick={() => {
        // hide caret
        setFormulaCaretPosition(-1);
        setShowMetricSelector(false);
      }}
    >
      <ModalHeader onClose={toggleIsOpen}>
        <div className={styles.headerBar}>
          <h5 className={clsx(typography.title5, styles.headerText)}>
            {columnName ? (
              <Content id={ContentId.EditACustomColumn} />
            ) : (
              <Content id={ContentId.CreateACustomColumn} />
            )}
          </h5>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className={styles.bodyContainer}>
          <div className={styles.title}>
            <Content id={ContentId.Name} />
          </div>
          <PosFormField errors={errors.name?.message}>
            <PosTextField
              value={name}
              placeholder={nameColumnPlaceholder}
              {...register('name')}
              onChange={(e) => {
                clearErrors('name');
                // filter out invisible characters for safety. Important against sql injection
                const filteredValue = e.target.value.replace(
                  /[\u200B-\u200D\uFEFF]/g,
                  ''
                );
                setValue('name', filteredValue);
              }}
            />
          </PosFormField>
          <div className={styles.title}>
            <Content id={ContentId.FormulaBuilder} />

            <CustomColumnInstructionPopover />
          </div>
          <FormProvider {...methods}>
            <FormulaBuilderInput
              fieldOptionsReverse={fieldOptionsReverse}
              formulaTokens={formulaTokens}
              setFormulaTokens={setFormulaTokens}
              formulaCaretPosition={formulaCaretPosition}
              setFormulaCaretPosition={setFormulaCaretPosition}
              showMetricSelector={showMetricSelector}
              setShowMetricSelector={setShowMetricSelector}
              aggregateFunction={aggregateFunction}
              setAggregateFunction={setAggregateFunction}
            />
          </FormProvider>

          {hasRatioToReportFeature && (
            <>
              <div className={styles.title}>
                <Content id={ContentId.WindowFunction} />
              </div>
              <PosFormField>
                <PosEnumSelect
                  value={selectedWindowFunction}
                  onChange={(value) => {
                    if (value) {
                      setSelectedWindowFunction(value);
                    }
                  }}
                  valueOptionsContent={WINDOW_FUNCTION_TO_CID}
                  style={{ width: '100%' }}
                />
              </PosFormField>
            </>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <CancelButton textContentId={ContentId.Cancel} onClick={toggleIsOpen} />
        <OkButton
          onClick={handleSubmit(onSaveHandler)}
          textContentId={ContentId.Save}
        />
      </ModalFooter>
    </Modal>
  );
}
